// Peças compartilhadas para gerar uma folha (Tabela de Preços, Planilha de
// Fechamento…): carregar a logo, desenhar o cabeçalho navy + faixa arco-íris,
// baixar como JPEG e imprimir/PDF (canvas → <img> → @page { margin: 0 }).

import { el } from "../ui.ts";

export const LOGO_SRC = "assets/logo-dark.svg";

/**
 * Escala de texto dos documentos, em px na largura lógica da folha (~520px,
 * exportada em 3×) — seção "Tipografia" de docs/design/ui.md. Documento
 * novo escolhe o papel, nunca um tamanho solto.
 */
export const TAM_DOC = {
  numero: 20, // número-total no cabeçalho
  titulo: 15, // título do documento
  lista: 13, // corpo da Tabela de Preços (lida no celular), TOTAL GERAL
  corpo: 10, // corpo das tabelas, subtítulo, cabeçalho de coluna (700)
  chip: 9, // etiqueta do Cronograma, slogan do rodapé
  rotulo: 8, // rótulo em caixa alta (700), nota de rodapé (itálico)
} as const;

export type EstiloFonteDoc = "" | "700" | "italic" | "italic 700";

/**
 * `ctx.font` de um papel da escala. A família é a do app, lida do CSS
 * (`--font-sans`) na hora de desenhar — mesmo caminho de `cor()`.
 */
export function fonteDoc(papel: keyof typeof TAM_DOC, estilo: EstiloFonteDoc = ""): string {
  const familia = getComputedStyle(document.documentElement).getPropertyValue("--font-sans").trim();
  return `${estilo ? estilo + " " : ""}${TAM_DOC[papel]}px ${familia}`;
}

export function carregarLogo(): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = LOGO_SRC;
  });
}

// --- cabeçalho de folha exportada (faixa azul) ----------------------------
//
// Diferente do cabeçalho padrão: a logo vai menor e à esquerda, o título em
// branco ao lado dela, e os dados que ficavam soltos acima da tabela (na
// Fatura: Cliente, Período, Vencimento, data, total) entram na própria faixa
// azul. Abaixo da faixa fica só a tabela e o rodapé de marca. `itens` e `total`
// são opcionais — sem `itens` a faixa é mais baixa (Tabela de Preços,
// Cronograma).

/** Tokens de cor do CSS que os documentos em canvas usam. */
export type TokenCor =
  | "ink" | "muted" | "brand-navy" | "brand-navy-deep" | "surface" | "surface-alt" | "border"
  | "ok" | "tint-ok" | "danger" | "tint-danger"
  | "rb-red" | "rb-amber" | "rb-green" | "rb-blue" | "rb-violet";

/**
 * Cor de um token do CSS (`--nome`), lida na hora de desenhar: o documento
 * impresso usa exatamente as cores da tela, sem repetir hexadecimal (uma
 * cópia em hexa já deixou a devolução do Cronograma num verde diferente do
 * da tela). Nunca chamar no topo do módulo — só dentro do desenho, quando o
 * CSS já carregou.
 */
export function cor(token: TokenCor): string {
  return getComputedStyle(document.documentElement).getPropertyValue(`--${token}`).trim();
}

const arcoIris = (): string[] =>
  (["rb-red", "rb-amber", "rb-green", "rb-blue", "rb-violet"] as const).map(cor);

export interface CabecalhoFolha {
  /** Ex.: "FECHAMENTO". */
  titulo: string;
  /** Linha pequena sob o título. Ex.: "Fatura 2026/0042" ou "Data: 09/09/2026". */
  subtitulo?: string;
  /** Só na Fatura. Ex.: "R$ 3.802,60". */
  total?: string;
  /** Rótulo do valor à direita quando há `total`. Padrão: "TOTAL". */
  rotuloTotal?: string;
  /** Pares rótulo/valor da 2ª linha; o último vai à direita. Vazio = sem 2ª linha. */
  itens?: { rotulo: string; valor: string }[];
}

/** Altura da faixa azul + os 3 px da faixa arco-íris. */
export function alturaCabecalhoFolha(temItens: boolean): number {
  return (temItens ? 112 : 62) + 3;
}

/** Desenha a faixa azul da folha + a faixa arco-íris. Devolve o `y` abaixo. */
export function desenharCabecalhoFolha(
  ctx: CanvasRenderingContext2D,
  W: number,
  logo: HTMLImageElement | null,
  cab: CabecalhoFolha,
): number {
  const PAD = 32;
  const itens = cab.itens ?? [];
  const alturaTotal = alturaCabecalhoFolha(itens.length > 0);
  const H = alturaTotal - 3;

  ctx.save();
  ctx.fillStyle = cor("brand-navy-deep");
  ctx.fillRect(0, 0, W, H);

  const temItens = itens.length > 0;
  const logoY = temItens ? 18 : 11;
  const yT = temItens ? 33 : 26;
  const yS = temItens ? 50 : 43;

  // logo à esquerda, pequena (mesmo recorte do cabeçalho padrão)
  const logoH = 40;
  let xTitulo = PAD;
  if (logo) {
    const sw = 724;
    const sh = 726;
    const logoW = (logoH * sw) / sh;
    ctx.drawImage(logo, 150, 146, sw, sh, PAD, logoY, logoW, logoH);
    xTitulo = PAD + logoW + 16;
    ctx.strokeStyle = "rgba(255,255,255,.22)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(xTitulo - 8, logoY);
    ctx.lineTo(xTitulo - 8, logoY + logoH);
    ctx.stroke();
  }

  // largura reservada à direita para o total (0 quando não há)
  let wDir = 0;
  if (cab.total) {
    ctx.font = fonteDoc("numero", "700");
    wDir = ctx.measureText(cab.total).width + 40;
  }
  const maxTitulo = W - PAD - xTitulo - wDir;

  // título em branco + subtítulo
  ctx.textAlign = "left";
  ctx.fillStyle = cor("surface");
  if ("letterSpacing" in ctx) {
    (ctx as unknown as { letterSpacing: string }).letterSpacing = "1.4px";
  }
  ctx.font = fonteDoc("titulo", "700");
  ctx.fillText(cortar(ctx, cab.titulo, maxTitulo), xTitulo, yT);
  if ("letterSpacing" in ctx) {
    (ctx as unknown as { letterSpacing: string }).letterSpacing = "0px";
  }
  if (cab.subtitulo) {
    ctx.fillStyle = "rgba(255,255,255,.62)";
    ctx.font = fonteDoc("corpo");
    ctx.fillText(cortar(ctx, cab.subtitulo, maxTitulo), xTitulo, yS);
  }

  // total à direita (só na Fatura)
  if (cab.total) {
    ctx.textAlign = "right";
    ctx.fillStyle = "rgba(255,255,255,.55)";
    ctx.font = fonteDoc("rotulo", "700");
    ctx.fillText(cab.rotuloTotal ?? "TOTAL", W - PAD, 25);
    ctx.fillStyle = cor("surface");
    ctx.font = fonteDoc("numero", "700");
    ctx.fillText(cab.total, W - PAD, 48);
  }

  // segunda linha: rótulo/valor lado a lado, o último à direita
  if (temItens) {
    const yLinha = 66;
    ctx.strokeStyle = "rgba(255,255,255,.14)";
    ctx.beginPath();
    ctx.moveTo(PAD, yLinha);
    ctx.lineTo(W - PAD, yLinha);
    ctx.stroke();

    const rotFont = fonteDoc("rotulo", "700");
    const valFont = fonteDoc("corpo");
    let x = PAD;
    itens.forEach((it, i) => {
      const ultimo = i === itens.length - 1;
      const xr = ultimo ? W - PAD : x;
      ctx.textAlign = ultimo ? "right" : "left";

      ctx.font = rotFont;
      ctx.fillStyle = "rgba(255,255,255,.55)";
      const wRot = ctx.measureText(it.rotulo.toUpperCase()).width;
      ctx.fillText(it.rotulo.toUpperCase(), xr, yLinha + 15);

      ctx.font = valFont;
      ctx.fillStyle = cor("surface");
      const wVal = ctx.measureText(it.valor).width;
      ctx.fillText(it.valor, xr, yLinha + 30);

      x += Math.max(wRot, wVal) + 28;
    });
  }

  // faixa arco-íris = limite da faixa azul
  arcoIris().forEach((c, i) => {
    ctx.fillStyle = c;
    ctx.fillRect((W / 5) * i, H, W / 5 + 1, 3);
  });

  ctx.restore();
  return alturaTotal;
}

// --- rodapé de marca (slogan + WhatsApp), compartilhado ---------------------

/** viewBox 0 0 24 24 — silhueta do WhatsApp (simple-icons). */
const WHATSAPP_PATH =
  "M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.359.101 11.943c0 2.096.549 4.14 1.595 5.945L0 24l6.335-1.652a11.899 11.899 0 005.71 1.447h.006c6.585 0 11.946-5.359 11.949-11.945a11.86 11.86 0 00-3.5-8.404z";

/** Glifo do WhatsApp, branco. `tam` = lado em px. */
export function whatsappGlifo(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  tam: number,
): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(tam / 24, tam / 24);
  ctx.fillStyle = cor("surface");
  ctx.fill(new Path2D(WHATSAPP_PATH));
  ctx.restore();
}

/** Cama de hóspede (perfil), branca. `w` = largura em px. */
export function iconeCama(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
): void {
  const s = w / 24;
  ctx.save();
  ctx.translate(x, y);
  ctx.fillStyle = cor("surface");
  ctx.strokeStyle = cor("surface");
  ctx.lineWidth = 1.7 * s;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  ctx.moveTo(1.6 * s, 7 * s);
  ctx.lineTo(1.6 * s, 18.5 * s);
  ctx.moveTo(1.6 * s, 14.5 * s);
  ctx.lineTo(22.4 * s, 14.5 * s);
  ctx.lineTo(22.4 * s, 18.5 * s);
  ctx.stroke();
  ctx.beginPath();
  ctx.roundRect(1.6 * s, 10.5 * s, 20.8 * s, 4.2 * s, 1.5 * s);
  ctx.fill();
  ctx.beginPath();
  ctx.roundRect(3.4 * s, 7.6 * s, 6 * s, 3.4 * s, 1.3 * s);
  ctx.fill();
  ctx.restore();
}

/** Altura da faixa do rodapé de marca. */
export const ALTURA_RODAPE_MARCA = 42;

/**
 * Faixa navy no rodapé: ícone de cama + slogan à esquerda, WhatsApp à direita,
 * tudo numa linha só. `yTopo` = borda de cima da faixa.
 */
export function desenharRodapeMarca(
  ctx: CanvasRenderingContext2D,
  W: number,
  yTopo: number,
  rodape: { slogan: string; whatsapp: string },
): void {
  const PAD = 32;
  const H = ALTURA_RODAPE_MARCA;
  const midY = yTopo + H / 2;

  ctx.save();
  ctx.fillStyle = cor("brand-navy-deep");
  ctx.fillRect(0, yTopo, W, H);

  ctx.textBaseline = "middle";
  ctx.fillStyle = cor("surface");

  // WhatsApp à direita (mede primeiro para reservar o espaço do slogan)
  ctx.font = fonteDoc("corpo", "700");
  ctx.textAlign = "left";
  const tam = 13;
  const wtw = ctx.measureText(rodape.whatsapp).width;
  const blocoZap = tam + 6 + wtw;
  const bx = W - PAD - blocoZap;
  whatsappGlifo(ctx, bx, midY - tam / 2, tam);
  ctx.fillText(rodape.whatsapp, bx + tam + 6, midY + 0.5);

  // cama + slogan à esquerda
  const camaW = 18;
  iconeCama(ctx, PAD, midY - camaW / 2, camaW);
  ctx.font = fonteDoc("chip", "italic 700");
  const xSlogan = PAD + camaW + 8;
  const maxSlogan = bx - 12 - xSlogan;
  ctx.fillText(cortar(ctx, rodape.slogan, maxSlogan), xSlogan, midY + 0.5);

  ctx.restore();
  ctx.textBaseline = "alphabetic";
}

/**
 * Nome de todo arquivo exportado (padrão do dono, Fase 9):
 * `<módulo>-<cliente, se houver>-<aaaammdd>[.extensão]`, em slug minúsculo e
 * sem acento. Sem extensão, serve de título do documento impresso — é o nome
 * que o navegador sugere ao salvar o PDF.
 */
export function nomeArquivo(
  modulo: string,
  cliente: string | null,
  dataISO: string,
  extensao?: string,
): string {
  const slug = (s: string): string =>
    s
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  const partes = [modulo, cliente ? slug(cliente) : "", dataISO.replace(/-/g, "")].filter(Boolean);
  return partes.join("-") + (extensao ? `.${extensao}` : "");
}

/** Entrega um arquivo ao usuário. Único lugar que mexe com Blob + <a download>. */
export function baixarArquivo(nome: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = el("a", { href: url, download: nome }) as HTMLAnchorElement;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/** Texto como arquivo. O BOM faz o Excel abrir o CSV com acento certo. */
export function baixarTexto(
  nome: string,
  conteudo: string,
  mime = "text/csv;charset=utf-8",
): void {
  baixarArquivo(nome, new Blob(["﻿" + conteudo], { type: mime }));
}

export function baixarCanvasJpeg(cv: HTMLCanvasElement, nome: string): void {
  cv.toBlob(
    (blob) => {
      if (blob) baixarArquivo(nome, blob);
    },
    "image/jpeg",
    0.94,
  );
}

// --- medida de texto (usada por quem desenha tabela no canvas) --------------

/** `texto` cortado com reticências para caber em `maxLarg`. */
export function cortar(
  ctx: CanvasRenderingContext2D,
  texto: string,
  maxLarg: number,
): string {
  if (ctx.measureText(texto).width <= maxLarg) return texto;
  let t = texto;
  while (t.length > 1 && ctx.measureText(`${t}…`).width > maxLarg) {
    t = t.slice(0, -1);
  }
  return `${t}…`;
}

/** `texto` quebrado em quantas linhas precisar para caber em `maxLarg`. */
export function quebrar(
  ctx: CanvasRenderingContext2D,
  texto: string,
  maxLarg: number,
): string[] {
  const linhas: string[] = [];
  let atual = "";
  for (const palavra of texto.split(" ")) {
    const teste = atual ? `${atual} ${palavra}` : palavra;
    if (atual && ctx.measureText(teste).width > maxLarg) {
      linhas.push(atual);
      atual = palavra;
    } else {
      atual = teste;
    }
  }
  if (atual) linhas.push(atual);
  return linhas;
}

/**
 * `quebrar`, mas em no máximo `max` linhas: o que sobra é espremido na última.
 * Toda linha devolvida cabe em `maxLarg` — inclusive quando uma palavra sozinha
 * é mais larga que o espaço, caso em que ela sai cortada com reticências.
 */
export function quebrarAte(
  ctx: CanvasRenderingContext2D,
  texto: string,
  maxLarg: number,
  max: number,
): string[] {
  const linhas = quebrar(ctx, texto, maxLarg);
  const ate =
    linhas.length <= max
      ? linhas
      : [...linhas.slice(0, max - 1), linhas.slice(max - 1).join(" ")];
  return ate.map((l) => cortar(ctx, l, maxLarg));
}

function escaparHtml(s: string): string {
  return s.replace(
    /[&<>"]/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!,
  );
}

/**
 * Manda o canvas para a impressora / "Salvar como PDF". A folha vai dentro de um
 * `<iframe>` isolado — só a imagem e `@page { margin: 0 }` — e é o `contentWindow`
 * dele que imprime. Assim a folha impressa não depende de esconder o resto do
 * app por `@media print` (era frágil: corria com o re-render das telas e com o
 * layout ainda não aplicado quando o canvas é grande — o Cronograma saía com o
 * menu lateral no PDF).
 */
export function imprimirCanvas(cv: HTMLCanvasElement, titulo: string): void {
  const dataUrl = cv.toDataURL("image/jpeg", 0.95);
  // fora da tela mas renderizado — iframe com display:none ou visibility:hidden
  // não imprime em alguns navegadores
  const iframe = el("iframe", {
    "aria-hidden": "true",
    style: "position:fixed;left:-9999px;top:0;width:1px;height:1px;border:0;",
  }) as HTMLIFrameElement;

  const limpar = (): void => iframe.remove();

  document.body.append(iframe);
  const doc = iframe.contentDocument;
  const win = iframe.contentWindow;
  if (!doc || !win) return limpar();

  const t = escaparHtml(titulo);
  doc.open();
  doc.write(
    `<!doctype html><meta charset="utf-8"><title>${t}</title>` +
      `<style>@page{size:auto;margin:0}html,body{margin:0;padding:0}` +
      `img{display:block;width:100%;height:auto}</style>` +
      `<img alt="${t}" src="${dataUrl}">`,
  );
  doc.close();

  const img = doc.images[0];
  const paraImpressora = (): void => {
    win.addEventListener("afterprint", limpar, { once: true });
    setTimeout(limpar, 120000);
    win.focus();
    win.print();
  };
  const quandoPronta = (): void => {
    const decode = img?.decode ? img.decode().catch(() => {}) : Promise.resolve();
    void decode.then(paraImpressora);
  };

  if (!img || img.complete) quandoPronta();
  else {
    img.onload = quandoPronta;
    img.onerror = limpar;
  }
}
